import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import styles from './EditMeetingStatusDialog.module.scss';
import { getAbsoluteTimestamp } from '../../../../util/formatDate';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import LoadingButton from '@mui/lab/LoadingButton';
import FormAlert from '../FormAlert/FormAlert';
import { useNavigate, useParams } from 'react-router-dom';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import { DateTimePicker } from '@mui/x-date-pickers';
import APIClient from '../../../../util/APIClient';

const MEETING_STATUS_OPTIONS = [
  {
    key: 'ENDED',
    value: 'ENDED',
    label: 'Ended',
    description: 'Meeting has taken place.',
  },
  {
    key: 'MISSED',
    value: 'MISSED',
    label: 'Missed',
    description: 'Meeting was scheduled but did not happen',
  },
];

function EditMeetingStatusDialog({ isOpen, close, meeting, reload, path }) {
  const navigate = useNavigate();
  const [startTimeInput, setStartTimeInput] = React.useState(
    meeting.startTime ? new Date(meeting.startTime) : null,
  );
  const [endTimeInput, setEndTimeInput] = React.useState(
    meeting.endTime ? new Date(meeting.endTime) : null,
  );
  const [meetingStatusInput, setMeetingStatusInput] = React.useState(
    meeting.status,
  );
  const [timeError, setTimeError] = React.useState(null);
  const [formAlert, setFormAlert] = React.useState(null);
  const [hasStartTime, setHasStartTime] = React.useState(
    meeting.status !== 'MISSED',
  );
  const [hasEndTime, setHasEndTime] = React.useState(
    meeting.status !== 'MISSED',
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    setStartTimeInput(meeting.startTime ? new Date(meeting.startTime) : null);
    setEndTimeInput(meeting.endTime ? new Date(meeting.endTime) : null);
    setMeetingStatusInput(meeting.status);
    setHasStartTime(meeting.status !== 'MISSED');
    setHasEndTime(meeting.status !== 'MISSED');
  }, [meeting]);

  React.useEffect(() => {
    if (
      startTimeInput &&
      endTimeInput &&
      startTimeInput.getTime() >= endTimeInput.getTime()
    ) {
      setTimeError('Meeting cannot end before its start time.');
      return;
    }
    setTimeError(null);
  }, [startTimeInput, endTimeInput]);

  React.useEffect(() => {
    if (meetingStatusInput === 'MISSED') {
      setHasStartTime(false);
      setHasEndTime(false);
      setStartTimeInput(null);
      setEndTimeInput(null);
    } else {
      setHasStartTime(true);
      setHasEndTime(true);
      setStartTimeInput(new Date(meeting.scheduledTime));
      setEndTimeInput(
        new Date(new Date(meeting.scheduledTime).getTime() + 1800000),
      );
    }
  }, [meetingStatusInput]);

  const handleClose = () => {
    setStartTimeInput(meeting.startTime ? new Date(meeting.startTime) : null);
    setEndTimeInput(meeting.endTime ? new Date(meeting.endTime) : null);
    setMeetingStatusInput(meeting.status);
    setFormAlert(null);
    setIsSubmitting(false);
    close();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await APIClient.patch(path, {
        startTime: startTimeInput,
        endTime: endTimeInput,
      });
      reload();
      handleClose();
    } catch {
      setFormAlert({
        severity: 'error',
        title: 'Could not update meeting!',
        message:
          'Something went wrong when updating the meeting. Please contact the administrator for further support.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await APIClient.delete(path);
      navigate('../../');
      handleClose();
    } catch (e) {
      console.error(e);
      setFormAlert({
        severity: 'error',
        title: 'Could not delete meeting!',
        message:
          'Something went wrong when deleting the meeting. Please contact the administrator for further support.',
      });
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAbort = () => {
    handleClose();
  };

  const handleFocusLost = () => {
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleFocusLost}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Change Status</DialogTitle>
      <div className={styles.content}>
        <DialogContentText id="alert-dialog-description">
          You are currently editing the meeting status for Team &quot;
          {meeting.team.label}&quot;, originally scheduled for{' '}
          {getAbsoluteTimestamp(new Date(meeting.scheduledTime), true, true)}.
        </DialogContentText>
        <div className={styles.form}>
          <FormControl fullWidth>
            <InputLabel id="meeting-status-label">Meeting Status</InputLabel>
            <Select
              variant="outlined"
              labelId="meeting-status-label"
              id="meeting-status"
              label="Meeting status"
              onChange={(e) => setMeetingStatusInput(e.target.value)}
              value={meetingStatusInput}
              renderValue={(selected) =>
                MEETING_STATUS_OPTIONS.filter((o) => o.value === selected)?.[0]
                  ?.label
              }
            >
              {MEETING_STATUS_OPTIONS.map((o) => (
                <Tooltip
                  key={o.key}
                  title={
                    <p>
                      <strong>{o.label}</strong> - <em>{o.description}</em>
                    </p>
                  }
                  placement="right"
                  value={o.value}
                >
                  <MenuItem disabled={o.value === 'JOINED'} key={o.key}>
                    {o.label}
                  </MenuItem>
                </Tooltip>
              ))}
            </Select>
          </FormControl>
          <FormControl
            sx={{ m: 1 }}
            // disabled={!isEditable}
            className={styles.formControl}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                id="start-time-input"
                value={startTimeInput}
                label="Start time"
                inputFormat="dd/MM/yyyy HH:mm"
                format="dd/MM/yyyy HH:mm"
                minDateTime={new Date(meeting.minTime)}
                maxDateTime={new Date(meeting.maxTime)}
                ampm={false}
                disabled={!hasStartTime}
                onChange={(value) => setStartTimeInput(value)}
                slotProps={{
                  textField: {
                    className: styles.input,
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                id="end-time-input"
                value={endTimeInput}
                label="End time"
                inputFormat="dd/MM/yyyy HH:mm"
                format="dd/MM/yyyy HH:mm"
                minDateTime={new Date(meeting.minTime)}
                maxDateTime={new Date(meeting.maxTime)}
                ampm={false}
                disabled={!hasEndTime}
                onChange={(value) => setEndTimeInput(value)}
                slotProps={{
                  textField: {
                    className: styles.input,
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </LocalizationProvider>
          </FormControl>
          {timeError && <FormHelperText error>{timeError}</FormHelperText>}
        </div>
        <div className={styles.buttons}>
          <LoadingButton
            loading={isSubmitting}
            color="error"
            variant="outlined"
            tabIndex={-1}
            onClick={handleDelete}
          >
            Delete
          </LoadingButton>
          <div className={styles.subButtons}>
            <Button onClick={handleAbort} color="primary" variant="outlined">
              Cancel
            </Button>
            <LoadingButton
              loading={isSubmitting}
              color="primary"
              variant="contained"
              type="submit"
              disabled={!!timeError}
              onClick={handleSubmit}
            >
              Update
            </LoadingButton>
          </div>
        </div>
        {formAlert && <FormAlert alert={formAlert} />}
      </div>
    </Dialog>
  );
}

/**
 * Must be placed within a path that contains the teamId and meetingId parameters.
 */
export function TeamEditMeetingStatusDialog(props) {
  const { meetingId, teamId } = useParams();
  return (
    <EditMeetingStatusDialog
      {...props}
      path={`/user/teams/${teamId}/meetings/${meetingId}`}
    />
  );
}

/**
 * Must be placed within a path that contains the teamId and meetingId parameters.
 */
export function AdministratorEditMeetingStatusDialog(props) {
  const { meetingId, teamId } = useParams();
  return (
    <EditMeetingStatusDialog
      {...props}
      path={`/administrator/teams/${teamId}/meetings/${meetingId}`}
    />
  );
}
